import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { BLUR_CSS, toggleDemoBlur, useDemoBlur } from "@/lib/demoBlur";
import { featureForKey, flipFeature } from "@/lib/demoKeys";
import { Switch } from "@/components/ui/switch";

// v84 — the owner's one site-wide demo blur. v85 — a plain on/off eye + Shift+Space; nothing else.
const onAdmin = (path) => path === "/admin" || path.startsWith("/admin/");

const flip = () => toggleDemoBlur().catch(() => toast.error("Demo blur could not be changed. Please try again."));

const typing = (el) => !!el && (el.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName)
  || !!el.closest?.("[role=menu], [role=listbox], [role=combobox], [role=dialog] input, [cmdk-root]"));

// Same switch, for the Creator 360 Admin menu and the Feature access page.
export function BlurSwitch({ blur, prefix }) {
  const [busy, setBusy] = useState(false);
  return (
    <label className="flex items-center gap-2.5 text-sm text-zinc-200 cursor-pointer">
      <Switch checked={blur.on} disabled={busy} onCheckedChange={() => { setBusy(true); flip().finally(() => setBusy(false)); }}
        className="data-[state=checked]:bg-amber-400" data-testid={`${prefix}-blur-switch`} />
      <span>Demo blur<span className="block text-[11px] text-zinc-500">Blurs every creator on your screen · Shift+Space</span></span>
    </label>
  );
}

// Top bar eye icon, owner only (the blur state never loads for anyone else).
export function DemoBlurNav() {
  const { blur } = useDemoBlur();
  const owner = !!blur;
  useEffect(() => {
    if (!owner) return undefined;
    const onKey = (e) => {
      if (!e.shiftKey || e.ctrlKey || e.metaKey || e.altKey || e.repeat) return;
      const feat = featureForKey(e.code);
      if (e.code !== "Space" && !feat) return;
      if (typing(e.target) || typing(document.activeElement)) return;
      e.preventDefault();
      if (feat) flipFeature(feat).catch(() => {});  // v86 — silent: no toast, nothing on screen
      else flip();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [owner]);
  if (!blur) return null;
  const tip = `Demo blur ${blur.on ? "on" : "off"} · Shift+Space`;
  return (
    <button type="button" onClick={flip} data-testid="nav-demo-blur" data-on={blur.on ? "1" : "0"} title={tip} aria-label={tip} aria-pressed={blur.on}
      className={`p-1 rounded-md border transition-colors duration-150 ${blur.on ? "text-amber-300 border-amber-400/60 bg-amber-400/10" : "text-zinc-500 border-transparent hover:text-white"}`}>
      {blur.on ? <EyeOff size={14} /> : <Eye size={14} />}
    </button>
  );
}

// The page-wide CSS blur; never on admin pages.
export function DemoBlurStyle() {
  const { blur } = useDemoBlur();
  const { pathname } = useLocation();
  if (!blur?.on || onAdmin(pathname)) return null;
  return <style data-testid="demo-blur-style">{BLUR_CSS}</style>;
}
